import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { RecipeItemType } from "../../model/nutritionPlanTypes";
import { User_Friendly_Units } from "../../model/userFriendlyUnits";

type Props = {
  ingredients: RecipeItemType[];
};

const GRAMS_UNIT = 18;

type Row = {
  id: string;
  name: string;
  /** "(150 gr)" when the chosen unit is not grams. */
  gramsText: string | null;
  quantity: number;
  unit: string;
};

/**
 * Ingredient rows, with the mobile rule: unit 18 (grams) shows the gram
 * quantity alone; any other unit shows "name (X gr)" and the serving count.
 */
const toRow = (item: RecipeItemType): Row => {
  const food = item.food;
  if (!food) {
    return {
      id: item.id,
      name: "Unknown ingredient",
      gramsText: null,
      quantity: 0,
      unit: "grams",
    };
  }
  const grams = Math.round(food.quantity || 0);
  const servings = food.number_of_servings || 1;
  const selected = food.user_friendly_units?.find(
    (unit) => unit.id === food.selected_user_friendly_unit_id
  );
  const unitEntry =
    selected && User_Friendly_Units[selected.user_friendly_unit];
  const isGrams = !unitEntry || selected?.user_friendly_unit === GRAMS_UNIT;
  return {
    id: item.id,
    name: food.name,
    gramsText: isGrams ? null : `(${grams} gr)`,
    quantity: isGrams ? grams : servings,
    unit: isGrams ? "grams" : unitEntry.label.toLowerCase(),
  };
};

const formatQuantity = (quantity: number): string =>
  quantity % 1 === 0 ? quantity.toString() : quantity.toFixed(2);

const RecipeIngredientsList = ({ ingredients }: Props) => {
  const theme: Theme = useTheme();
  const rows = ingredients.map(toRow);

  if (rows.length === 0) {
    return <Box sx={styles.empty(theme)}>No ingredients available.</Box>;
  }

  return (
    <Box sx={styles.list}>
      {rows.map((row) => (
        <Box key={row.id} sx={styles.row(theme)}>
          <Box sx={styles.name}>
            {row.name}
            {row.gramsText && (
              <Box component="span" sx={styles.grams(theme)}>
                {" "}
                {row.gramsText}
              </Box>
            )}
          </Box>
          <Box sx={styles.quantity(theme)}>
            {formatQuantity(row.quantity)} {row.unit}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default RecipeIngredientsList;

const styles = {
  list: {
    display: "flex",
    flexDirection: "column" as "column",
  },
  row: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    padding: "10px 0",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  name: {
    flex: 1,
  },
  grams: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  quantity: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    whiteSpace: "nowrap" as "nowrap",
  }),
  empty: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
  }),
};
